import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import { Assessment, Download, ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generateReport, clearError, clearReports } from '../../store/strategistSlice';
import { searchSprints } from '../../store/ticketSlice';
import { REPORT_TYPES, DATE_RANGES, DATE_RANGE_LABELS } from '../../shared/appConstants';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { exportAgentReportToPDF, formatReportAsText, flattenToKeyValueRows, processReportData } from './agentReportExport';
import './StrategistAgent.scss';

const AgentReportViewer = ({ projectId, resetTrigger = 0, refreshTrigger = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reports, loading, error } = useSelector((state) => state.strategist);
  const { searchSprintsLoading } = useSelector((state) => state.tickets);

  const [reportType, setReportType] = useState(REPORT_TYPES.SPRINT_SUMMARY);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [sprintOptions, setSprintOptions] = useState([]);
  const [suggestedSprints, setSuggestedSprints] = useState([]);
  const [sprintSearchTerm, setSprintSearchTerm] = useState('');
  const [loadingSuggestedSprints, setLoadingSuggestedSprints] = useState(false);
  const [timeframe, setTimeframe] = useState(DATE_RANGES.SELECT_TIME);
  const [format, setFormat] = useState('json');
  const [requirement, setRequirement] = useState('');
  const [jsonExpanded, setJsonExpanded] = useState({});
  const [textExpanded, setTextExpanded] = useState({});
  const lastProjectIdRef = React.useRef(projectId);
  const lastRefreshTriggerRef = React.useRef(refreshTrigger);

  const loadInitialSprints = useCallback(async () => {
    if (projectId) {
      setLoadingSuggestedSprints(true);
      try {
        const result = await dispatch(searchSprints({ projectId, searchTerm: '', limit: 10 })).unwrap();
        setSuggestedSprints(result);
      } catch (error) {
        setSuggestedSprints([]);
      }
      setLoadingSuggestedSprints(false);
    }
  }, [projectId, dispatch]);

  useEffect(() => {
    loadInitialSprints();
  }, [loadInitialSprints]);

  useEffect(() => {
    const delayedSearch = setTimeout(async () => {
      if (sprintSearchTerm.length >= 1) {
        try {
          const result = await dispatch(searchSprints({ projectId, searchTerm: sprintSearchTerm, limit: 10 })).unwrap();
          setSprintOptions(result);
        } catch (error) {
          setSprintOptions([]);
        }
      } else {
        setSprintOptions([]);
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [sprintSearchTerm, projectId, dispatch]);

  useEffect(() => {
    if (resetTrigger > 0) {
      setReportType(REPORT_TYPES.SPRINT_SUMMARY);
      setSelectedSprint(null);
      setSprintSearchTerm('');
      setSprintOptions([]);
      setTimeframe(DATE_RANGES.SELECT_TIME);
      setFormat('json');
      setRequirement('');
      dispatch(clearReports());
    }
  }, [resetTrigger, dispatch]);

  useEffect(() => {
    if (lastProjectIdRef.current && projectId && lastProjectIdRef.current !== projectId) {
      setReportType(REPORT_TYPES.SPRINT_SUMMARY);
      setSelectedSprint(null);
      setSprintSearchTerm('');
      setSprintOptions([]);
      setTimeframe(DATE_RANGES.SELECT_TIME);
      setFormat('json');
      setRequirement('');
      dispatch(clearReports());
    }
    lastProjectIdRef.current = projectId;
  }, [projectId, dispatch]);

  const handleDateRangeChange = (value) => {
    setSelectedSprint(null);
    setTimeframe(value);
  };

  const handleSprintChange = (value) => {
    setSelectedSprint(value);
    if (value) {
      setTimeframe(DATE_RANGES.SELECT_TIME);
    }
  };

  const handleGenerate = useCallback(async () => {
    if (!projectId) {
      toast.error(t('agent.reports.projectRequired'));
      return;
    }

    const hasDateFilter = (timeframe && timeframe !== DATE_RANGES.SELECT_TIME);
    const hasSprintFilter = !!selectedSprint;

    if (hasSprintFilter && hasDateFilter) {
      toast.error(t('agent.reports.cannotUseBothFilters'));
      return;
    }

    try {
      const options = {
        sprintId: selectedSprint?.id || null,
        format,
        requirement: reportType === REPORT_TYPES.CUSTOM ? requirement : null,
      };

      if (timeframe && timeframe !== DATE_RANGES.SELECT_TIME) {
        options.timeframe = timeframe;
      } else {
        options.timeframe = null;
      }

      await dispatch(
        generateReport({
          projectId,
          reportType,
          options,
        })
      ).unwrap();

      toast.success(t('agent.reports.generated'));
    } catch (err) {
      toast.error(t('agent.reports.generationFailed'));
      console.error('Failed to generate report:', err);
    }
  }, [
    projectId,
    dispatch,
    reportType,
    selectedSprint,
    timeframe,
    format,
    requirement,
    t,
  ]);

  useEffect(() => {
    if (refreshTrigger === lastRefreshTriggerRef.current) return;
    lastRefreshTriggerRef.current = refreshTrigger;
    if (refreshTrigger > 0) {
      const hasDateFilter = (timeframe && timeframe !== DATE_RANGES.SELECT_TIME);
      const hasSprintFilter = !!selectedSprint;
      if (!hasDateFilter && !hasSprintFilter) return;
      handleGenerate();
    }
  }, [refreshTrigger, handleGenerate, timeframe, selectedSprint]);

  const getCurrentReport = () => {
    const reportKey = `${reportType}_${projectId}`;
    return reports[reportKey];
  };

  const formatLabel = useCallback((key) => {
    if (!key) return '';
    return String(key)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, []);

  const formatJsonPrimitive = (value) => {
    if (typeof value === 'string') return `"${value}"`;
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    return String(value);
  };

  const renderJsonTree = useCallback((value, path = 'root', depth = 0, isRoot = true, keyLabel = null) => {
    const isObject = value !== null && typeof value === 'object';
    const isArray = Array.isArray(value);
    const hasChildren = isObject && (isArray ? value.length > 0 : Object.keys(value || {}).length > 0);
    const isExpanded = isRoot || (jsonExpanded[path] ?? true);
    const labelText = keyLabel !== null ? `"${keyLabel}": ` : '';
    const openChar = isArray ? '[' : '{';
    const closeChar = isArray ? ']' : '}';

    if (isRoot) {
      if (!isObject) {
        return [
          <div className="report-json-line" style={{ paddingLeft: 0 }} key={`${path}-root-primitive`}>
            {formatJsonPrimitive(value)}
          </div>
        ];
      }
      const entries = isArray ? value.map((item, idx) => [idx, item]) : Object.entries(value || {});
      const rootLines = [];
      entries.forEach(([childKey, childValue]) => {
        const childPath = `${path}.${childKey}`;
        const childLabel = isArray ? `[${childKey}]` : childKey;
        rootLines.push(...renderJsonTree(childValue, childPath, 0, false, childLabel));
      });
      return rootLines;
    }

    const lines = [];

    if (!isObject) {
      lines.push(
        <div className="report-json-line" style={{ paddingLeft: depth * 12 }} key={`${path}-primitive`}>
          {labelText}{formatJsonPrimitive(value)}
        </div>
      );
      return lines;
    }

    if (!hasChildren) {
      lines.push(
        <div className="report-json-line" style={{ paddingLeft: depth * 12 }} key={`${path}-empty`}>
          <span className="report-toggle-spacer" />
          <span className="report-json-key">
            {labelText}{openChar}{closeChar}
          </span>
        </div>
      );
      return lines;
    }

    lines.push(
      <div className="report-json-line" style={{ paddingLeft: depth * 12 }} key={`${path}-open`}>
        <button
          type="button"
          className="report-toggle"
          onClick={() => setJsonExpanded((prev) => ({ ...prev, [path]: !isExpanded }))}
          aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
        >
          {isExpanded ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
        </button>
        <span className="report-json-key">
          {labelText}{openChar}
          {!isExpanded ? ` ${isArray ? `... (${value.length})` : '...'} ${closeChar}` : ''}
        </span>
      </div>
    );

    if (isExpanded) {
      const entries = isArray ? value.map((item, idx) => [idx, item]) : Object.entries(value || {});
      entries.forEach(([childKey, childValue]) => {
        const childPath = `${path}.${childKey}`;
        const childLabel = isArray ? `[${childKey}]` : childKey;
        lines.push(...renderJsonTree(childValue, childPath, depth + 1, false, childLabel));
      });
      lines.push(
        <div className="report-json-line" style={{ paddingLeft: depth * 12 }} key={`${path}-close`}>
          <span className="report-toggle-spacer" />
          {closeChar}
        </div>
      );
    }

    return lines;
  }, [jsonExpanded]);

  const renderTextTree = useCallback((value, path = 'root', depth = 0, isRoot = true, keyLabel = null, parentIndent = 0) => {
    const isNumericValue = (val) => {
      if (typeof val === 'number' && Number.isFinite(val)) return true;
      if (typeof val !== 'string') return false;
      const trimmed = val.trim();
      if (!trimmed) return false;
      return /^-?\d+(\.\d+)?%?$/.test(trimmed);
    };
    const isObject = value !== null && typeof value === 'object';
    const isArray = Array.isArray(value);
    const childCount = isObject ? (isArray ? value.length : Object.keys(value || {}).length) : 0;
    const hasChildren = isObject && childCount > 0;
    const isExpanded = isRoot || (textExpanded[path] ?? (depth === 0));
    const noneLabel = t('agent.reports.none');

    if (!isObject) {
      if (keyLabel === null) {
        return null;
      }
      const isRootNameField = depth === 0 && String(keyLabel).toLowerCase().includes('name');
      const isNumeric = isNumericValue(value);
      return (
        <div className="report-text-field" style={{ paddingLeft: parentIndent + 8 }} key={`${path}-field`}>
          <span className={isRootNameField ? 'report-text-label-bold' : 'report-text-label'}>
            {formatLabel(keyLabel)}:
          </span>{' '}
          <span
            className={
              isRootNameField
                ? 'report-text-value-bold'
                : (isNumeric ? 'report-text-value-emphasis' : 'report-text-value')
            }
          >
            {value === null || value === undefined || value === '' ? 'N/A' : String(value)}
          </span>
        </div>
      );
    }

    const shouldShowLine = hasChildren && childCount > 1;
    const indentUnit = 8;
    const toggleOffset = 24; // toggle width (16) + gap (8)
    const nestedIndentOffset = 4;
    const headerIndent = depth === 0 ? 0 : parentIndent + 8 + nestedIndentOffset;
    const contentIndent = parentIndent + indentUnit + (depth > 0 ? nestedIndentOffset : 0);

    if (isRoot) {
      const rootChildIndent = toggleOffset;
      const entries = Object.entries(value || {});
      return entries.map(([childKey, childValue]) =>
        renderTextTree(childValue, `${path}.${childKey}`, depth, false, childKey, rootChildIndent)
      );
    }
    const nestedLineOffset = 6;
    const rootLineOffset = 6;
    const lineLeft = Math.max(contentIndent - 6, 2) + (depth > 0 ? nestedLineOffset : rootLineOffset);
    const lineWidth = 2;
    const lineOpacity = Math.max(1 - depth * 0.3, 0.2);
    const sectionClasses = [
      'report-text-section',
      depth === 0 ? 'report-text-section-root' : '',
      shouldShowLine ? 'report-text-section-lined' : '',
      isArray ? 'report-text-section-array' : 'report-text-section-object',
    ].filter(Boolean).join(' ');

    if (!hasChildren) {
      return (
        <div className={sectionClasses} key={`${path}-empty-section`}>
          <div className="report-text-section-header" style={{ paddingLeft: headerIndent }}>
            <span className="report-text-heading">{formatLabel(keyLabel)}</span>
            <span className="report-text-heading-colon">:</span>
            <span className="report-text-empty-inline">{noneLabel}</span>
          </div>
        </div>
      );
    }

    return (
      <div
        className={sectionClasses}
        key={`${path}-section`}
        style={shouldShowLine ? { '--report-line-left': `${lineLeft}px`, '--report-line-width': `${lineWidth}px`, '--report-line-opacity': lineOpacity } : undefined}
      >
        <div className="report-text-section-header" style={{ paddingLeft: headerIndent }}>
          {depth === 0 ? (
            hasChildren ? (
              <button
                type="button"
                className="report-toggle"
                onClick={() => setTextExpanded((prev) => ({ ...prev, [path]: !isExpanded }))}
                aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
              >
                {isExpanded ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
              </button>
            ) : null
          ) : null}
          <span className="report-text-heading">{formatLabel(keyLabel)}</span>
          {depth > 0 && hasChildren && (
            <button
              type="button"
              className="report-toggle"
              onClick={() => setTextExpanded((prev) => ({ ...prev, [path]: !isExpanded }))}
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            >
              {isExpanded ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
            </button>
          )}
        </div>
        {isExpanded && (
          <div className="report-text-section-content">
            {isArray ? (
              value.length === 0 ? (
                <div className="report-text-empty" style={{ paddingLeft: contentIndent }}>
                  {noneLabel}
                </div>
              ) : (
                value.map((item, idx) => {
                  const itemPath = `${path}[${idx}]`;
                  const itemLabel = `Item ${idx + 1}`;
                  if (item !== null && typeof item === 'object') {
                    return renderTextTree(item, itemPath, depth + 1, false, itemLabel, contentIndent);
                  }
                  return (
                    <div
                      className="report-text-bullet"
                      style={{ paddingLeft: contentIndent }}
                      key={`${itemPath}-bullet`}
                    >
                      • {item === null || item === undefined || item === '' ? 'N/A' : String(item)}
                    </div>
                  );
                })
              )
            ) : (
              Object.entries(value || {}).map(([childKey, childValue]) =>
                renderTextTree(childValue, `${path}.${childKey}`, depth + 1, false, childKey, contentIndent)
              )
            )}
          </div>
        )}
      </div>
    );
  }, [textExpanded, formatLabel, t]);

  const handleExportToPDF = () => {
    const report = getCurrentReport();
    if (!report || !report.data) return;

    const finalData = processReportData(report.data);

    let content = '';
    if (format === 'json') {
      content = JSON.stringify(finalData, null, 2);
    } else if (format === 'text') {
      content = formatReportAsText(finalData, 0, 0);
    }

    exportAgentReportToPDF({
      content,
      reportType,
      format,
    });
  };

  const handleExportToExcel = () => {
    const report = getCurrentReport();
    if (!report || !report.data) {
      toast.error(t('agent.reports.noReportToExport'));
      return;
    }

    const finalData = processReportData(report.data);

    const rows = flattenToKeyValueRows(finalData);
    const metricLabel = t('agent.reports.metric');
    const valueLabel = t('agent.reports.value');
    const sheetData = rows.map((row) => ({ [metricLabel]: row.name, [valueLabel]: row.value }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const fileName = `report_${reportType}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('agent.reports.exportSuccess'));
  };

  const renderReport = () => {
    const report = getCurrentReport();
    if (!report || !report.data) return null;

    const finalData = processReportData(report.data);

    if (format === 'table') {
      const rows = flattenToKeyValueRows(finalData);

      return (
        <Box className="report-output report-output-table">
          <TableContainer className="report-table-container" component={Paper}>
            <Table className="report-table" size="small" stickyHeader>
              <TableHead>
                <TableRow className="report-table-head-row">
                  <TableCell className="report-table-head-cell">{t('agent.reports.metric')}</TableCell>
                  <TableCell className="report-table-head-cell" align="right">{t('agent.reports.value')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex} className="report-table-body-row">
                    <TableCell className="report-table-body-cell" sx={{ pl: 2 + (row.depth || 0) * 2.5 }}>
                      {row.name}
                    </TableCell>
                    <TableCell className="report-table-body-cell" align="right">
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    }

    if (format === 'text') {
      const textContent = formatReportAsText(finalData, 0, 0);
      return (
        <Box className="report-output report-output-text">
          <div className="report-text-content">
            {renderTextTree(finalData)}
          </div>
          <div
            className="report-html-content report-html-content-export"
            dangerouslySetInnerHTML={{ __html: textContent }}
          />
        </Box>
      );
    }

    return (
      <Box className="report-output report-output-json">
        <div className="report-json-tree">
          {renderJsonTree(finalData)}
        </div>
      </Box>
    );
  };

  return (
    <Box className="reports-container">
      <Paper elevation={0} className="reports-paper">
        <Box className="reports-header">
          <Box className="reports-header-content">
            <Typography variant="h6" className="reports-header-title reports-header-title-styled">
              {t('agent.reports.title')}
            </Typography>
          </Box>
        </Box>

      {error && (
        <Alert severity="error" className="reports-error-alert" onClose={() => dispatch(clearError())}>
          {error.message || error}
        </Alert>
      )}

      <Box className="reports-form">
        <Box className="reports-form-row">
          <Box className="reports-form-field-group">
            <Typography variant="body2" className="reports-field-label">
              {t('agent.reports.reportType')}
            </Typography>
            <FormControl className="reports-form-control" size="small">
              <Select 
                value={reportType} 
                onChange={(e) => setReportType(e.target.value)}
                displayEmpty
                disabled={!!getCurrentReport()}
              >
                <MenuItem value={REPORT_TYPES.SPRINT_SUMMARY}>{t('agent.reports.projectSummary')}</MenuItem>
                <MenuItem value={REPORT_TYPES.BLOCKERS}>{t('agent.reports.blockers')}</MenuItem>
                <MenuItem value={REPORT_TYPES.VELOCITY}>{t('agent.reports.velocity')}</MenuItem>
                {/* Future scope: Custom report option */}
                {/* <MenuItem value={REPORT_TYPES.CUSTOM}>{t('agent.reports.custom')}</MenuItem> */}
              </Select>
            </FormControl>
          </Box>

          <Box className="reports-form-field-group">
            <Typography variant="body2" className="reports-field-label">
              {t('agent.reports.format')}
            </Typography>
            <FormControl className="reports-form-control" size="small">
              <Select 
                value={format} 
                onChange={(e) => setFormat(e.target.value)}
                displayEmpty
              >
                <MenuItem value="json">JSON</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="table">Table</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box className="reports-form-row">
          <Box className="reports-form-field-group">
            <Typography variant="body2" className="reports-field-label">
              {t('agent.reports.sprints')}
            </Typography>
            <Autocomplete
              options={sprintSearchTerm.length >= 1 ? sprintOptions : suggestedSprints}
              value={selectedSprint}
              onChange={(event, newValue) => {
                handleSprintChange(newValue);
                if (!newValue) {
                  setSprintSearchTerm('');
                } else {
                  setSprintSearchTerm('');
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') {
                  setSprintSearchTerm(newInputValue);
                } else if (reason === 'clear') {
                  setSprintSearchTerm('');
                }
              }}
              loading={searchSprintsLoading || loadingSuggestedSprints}
              disabled={timeframe !== DATE_RANGES.SELECT_TIME || !!getCurrentReport()}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (typeof option === 'string') return option;
                return option?.name || option?.id || '';
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    {option.colour && (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: option.colour,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <OverflowTooltip
                      title={option.name}
                      tooltipWidth={400}
                      variant="body2"
                      maxLines={1}
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      {option.name}
                    </OverflowTooltip>
                  </Box>
                </Box>
              )}
              renderInput={(params) => {
                const isDisabled = timeframe !== DATE_RANGES.SELECT_TIME || !!getCurrentReport();
                const displayValue = selectedSprint?.name || '';
                
                return (
                  <OverflowTooltip
                    title={displayValue}
                    tooltipWidth={400}
                    placement="top-start"
                    component="div"
                    sx={{ width: '100%' }}
                  >
                    <TextField
                      {...params}
                      className="reports-text-input"
                      placeholder={isDisabled && !selectedSprint ? t('agent.reports.noSprintSelected') : t('agent.reports.sprintsPlaceholder')}
                      InputLabelProps={{
                        shrink: isDisabled && !selectedSprint ? true : undefined,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: selectedSprint?.colour ? (
                          <Box
                            className="reports-sprint-color-dot reports-sprint-input-adornment"
                            style={{ backgroundColor: selectedSprint.colour }}
                          />
                        ) : null,
                        endAdornment: (
                          <>
                            {searchSprintsLoading || loadingSuggestedSprints ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  </OverflowTooltip>
                );
              }}
              noOptionsText={sprintSearchTerm.length >= 1 ? t('agent.reports.noSprintsFound') : (loadingSuggestedSprints ? '' : t('agent.reports.noSuggestedSprints'))}
            />
          </Box>

          <Box className="reports-form-field-group">
            <Typography variant="body2" className="reports-field-label">
              {t('agent.reports.timeframe')}
            </Typography>
            <FormControl className="reports-form-control" size="small">
              <Select 
                value={timeframe}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                displayEmpty
                disabled={!!selectedSprint || !!getCurrentReport()}
              >
                <MenuItem value={DATE_RANGES.SELECT_TIME}>{DATE_RANGE_LABELS[DATE_RANGES.SELECT_TIME]}</MenuItem>
                <MenuItem value={DATE_RANGES.THIS_WEEK}>{DATE_RANGE_LABELS[DATE_RANGES.THIS_WEEK]}</MenuItem>
                <MenuItem value={DATE_RANGES.THIS_MONTH}>{DATE_RANGE_LABELS[DATE_RANGES.THIS_MONTH]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_MONTH}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_MONTH]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_7_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_7_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_14_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_14_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_30_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_30_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_60_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_60_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_90_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_90_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_120_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_120_DAYS]}</MenuItem>
                <MenuItem value={DATE_RANGES.LAST_180_DAYS}>{DATE_RANGE_LABELS[DATE_RANGES.LAST_180_DAYS]}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {reportType === REPORT_TYPES.CUSTOM && (
          <Box className="reports-form-row">
            <Box className="reports-form-field-group reports-form-field-group-full">
              <Typography variant="body2" className="reports-field-label">
                {t('agent.reports.requirement')}
              </Typography>
              <TextField
                value={requirement}
                onChange={(e) => setRequirement(e.target.value)}
                className="reports-text-input reports-requirement-input"
                size="small"
                multiline
                minRows={5}
                maxRows={8}
                placeholder={t('agent.reports.requirementPlaceholder')}
                fullWidth
                disabled={!!getCurrentReport()}
              />
            </Box>
          </Box>
        )}
      </Box>

      {!getCurrentReport() && (
        <Box className="reports-actions-container">
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!projectId || loading.generateReport || (reportType === REPORT_TYPES.CUSTOM && !requirement.trim()) || (timeframe === DATE_RANGES.SELECT_TIME && !selectedSprint)}
            startIcon={loading.generateReport ? <CircularProgress size={20} /> : <Assessment />}
            className="generate-report-button"
            sx={{
              opacity: (!projectId || loading.generateReport || (reportType === REPORT_TYPES.CUSTOM && !requirement.trim()) || (timeframe === DATE_RANGES.SELECT_TIME && !selectedSprint)) ? 0.5 : 1,
              cursor: (!projectId || loading.generateReport || (reportType === REPORT_TYPES.CUSTOM && !requirement.trim()) || (timeframe === DATE_RANGES.SELECT_TIME && !selectedSprint)) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading.generateReport ? t('agent.reports.generating') : t('agent.reports.generate')}
          </Button>
        </Box>
      )}

      {getCurrentReport() && (
        <>
          <Box className="reports-output-header">
            <Typography variant="subtitle2" color="text.secondary">
              {t('agent.reports.generatedAt')}: {(() => {
                const report = getCurrentReport();
                const ts = report?.generated_at ?? report?.generatedAt ?? report?.data?.generated_at ?? report?.data?.generatedAt;
                if (!ts) return '—';
                return typeof ts === 'string' ? ts : new Date(ts).toLocaleString();
              })()}
            </Typography>
            <Button 
              startIcon={<Download />} 
              size="small" 
              variant="outlined"
              onClick={format === 'table' ? handleExportToExcel : handleExportToPDF}
            >
              {format === 'table' ? t('agent.reports.exportToExcel') : t('agent.reports.exportToPDF')}
            </Button>
          </Box>
          {renderReport()}
        </>
      )}

      </Paper>
    </Box>
  );
};

export default AgentReportViewer;
